import { FunctionComponent } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TooltipItem,
  ChartType,
  ChartOptions,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import styles from './Graph.module.scss';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);
export type DataSet = {
  data: number[];
  borderColor: string;
  pointBorderColor?: string;
  pointBackgroundColor?: string;
  pointHoverBackgroundColor?: string;
  pointHoverBorderColor?: string;
};
export type GraphData = {
  labels: string[];
  datasets: DataSet[];
};

export type GraphProps = {
  data: GraphData;
  height?: string;
  formatHeader: (header: string) => string;
  formatLabel: (label: string) => string;
};
type FC = FunctionComponent<GraphProps>;

const Graph: FC = ({ data, height = '340', formatHeader, formatLabel }: GraphProps) => {
  const options: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
        position: 'top' as const,
      },
      title: {
        display: false,
        text: 'Chart.js Line Chart',
      },
      tooltip: {
        xAlign: 'center' as const,
        yAlign: 'bottom' as const,
        backgroundColor: '#FBFBFB',
        titleColor: '#2D2D2D',
        titleAlign: 'center' as const,
        titleFont: {
          size: 16,
          weight: 700,
        },
        titleMarginBottom: 0,
        footerColor: '#696974',
        footerAlign: 'center' as const,
        footerFont: {
          size: 14,
          weight: 300,
        },
        displayColors: false,

        padding: {
          left: 32,
          right: 32,
          top: 15,
          bottom: 15,
        },
        cornerRadius: 50,
        callbacks: {
          title(context: TooltipItem<ChartType>[]) {
            const title = context[0].dataset.data[context[0].dataIndex]?.toString() || '';
            return formatHeader(title.toString());
          },
          label() {
            return '';
          },
          footer(context: TooltipItem<ChartType>[]) {
            const body = context[0].label || '';
            return formatLabel(body.toString());
          },
        },
      },
    },
    layout: {
      padding: {
        top: 75,
        bottom: 0,
        right: 0,
        left: 0,
      },
      autoPadding: false,
    },
    scales: {
      x: {
        beginAtZero: true,
        border: {
          display: false,
        },
        grid: {
          display: true,
          color: '#FBFBFB',
          lineWidth: 2,
        },
        ticks: {
          color: '#92929D',
          padding: 35,
          font: {
            size: 14,
          },
          autoSkip: true,
          maxRotation: 0,
          minRotation: 0,
          maxTicksLimit: 10,
        },
      },
      y: {
        beginAtZero: true,
        border: {
          display: false,
        },
        grid: {
          display: false,
        },
        ticks: {
          color: '#92929D',
          padding: 35,
          autoSkip: true,
          maxTicksLimit: 5,
          font: {
            size: 14,
          },
        },
        suggestedMin: 0,
        min: 0,
      },
    },
    elements: {
      line: {
        tension: 0.4,
      },
      point: {
        hoverBorderWidth: 4,
        radius: 6,
        hoverRadius: 6,
        hitRadius: 300,
      },
    },
  };
  const dataSetOptions = {
    pointBorderColor: 'rgba(0, 0, 0, 0)',
    pointBackgroundColor: 'rgba(0, 0, 0, 0)',
    pointHoverBackgroundColor: 'rgb(0, 0, 0)',
    pointHoverBorderColor: '#FBFBFB',
  };
  const FormattedData = { ...data };
  FormattedData.datasets = data.datasets.map((dataset) => ({ ...dataset, ...dataSetOptions }));
  const plugin = [
    {
      id: 'point_line',
      afterDraw: (chart: ChartJS) => {
        if (chart.tooltip?.getActiveElements().length) {
          const { x } = chart.tooltip.getActiveElements()[0].element;
          const { y } = chart.tooltip.getActiveElements()[0].element;
          const yAxis = chart.scales.y;
          const { ctx } = chart;
          ctx.save();
          ctx.beginPath();
          ctx.moveTo(x, yAxis.bottom);
          ctx.lineTo(x, y);
          ctx.lineWidth = 1;
          ctx.strokeStyle = '#2D2D2D';
          ctx.stroke();
          ctx.restore();
        }
      },
    },
    {
      id: 'custom_canvas_background_color',
      beforeDraw: (chart: ChartJS) => {
        const { ctx } = chart;
        ctx.save();
        ctx.globalCompositeOperation = 'destination-over';
        ctx.fillStyle = '#F6F6F6';
        ctx.fillRect(0, 0, chart.width, chart.height);
        ctx.restore();
      },
    },
  ];
  return (
    <div className={styles.container}>
      <Line options={options} data={FormattedData} plugins={plugin} height={height} />
    </div>
  );
};
export default Graph;
